// Per-column section primitive — ports the MVP `Section.jsx`
// (immersive_gui/ui/src/components/Section.jsx) into MUI.
//
// Each exotics widget content renders one <Section>:
//   - Uppercase, letter-spaced title
//   - Optional loading dot in the header (pulsing accent)
//   - Optional right-aligned slot for icon buttons (used by Risk for the
//     ladder trigger)
//   - Collapsible body via chevron click
//
// Style notes:
//   - Header sits flush against the section's content; no extra padding.
//   - `defaultOpen={false}` is honoured for sections that should start
//     collapsed (Surface Fit and Model in the MVP).

import { FC, ReactNode, useState } from 'react';
import { Box, IconButton, Stack, Typography, keyframes } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`;

export interface SectionProps {
  title: string;
  loading?: boolean;
  defaultOpen?: boolean;
  right?: ReactNode;
  children: ReactNode;
}

const Section: FC<SectionProps> = ({
  title,
  loading = false,
  defaultOpen = true,
  right,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box sx={{ '&:not(:first-of-type)': { borderTop: 1, borderColor: 'divider' } }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setOpen((o) => !o)}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          py: 0.75,
          px: 0.5,
          // Avoid the chevron-click bubbling into the body's
          // EditableNumber `onFocus` handlers.
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              lineHeight: 1,
            }}
          >
            {title}
          </Typography>
          {loading && (
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                animation: `${pulse} 1s ease-in-out infinite`,
              }}
            />
          )}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          onClick={(e) => e.stopPropagation()}
        >
          {right}
          <IconButton
            size="small"
            aria-label={`toggle-${title}`}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((o) => !o);
            }}
            sx={{ p: 0.25 }}
          >
            <FontAwesomeIcon
              icon={faChevronDown}
              style={{
                fontSize: 9,
                transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.15s ease',
                color: 'inherit',
              }}
            />
          </IconButton>
        </Stack>
      </Stack>
      <Box
        sx={{
          maxHeight: open ? 2000 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.2s ease',
        }}
      >
        <Box sx={{ pt: 0.5, pb: 1 }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default Section;
